import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { CText, CHeader, CLoader, CButton } from '../../components/common';
import { FiCheck } from 'react-icons/fi';
import { fetchExpertDetails, createBookingOrder, createExpertBooking, Expert } from '../../api';

interface LocationState {
  expertId: string;
  selectedSlotDate: string;
  selectedSlotEndDate: string;
  bookingType: 'C' | 'V';
}

const BookingConfirmation: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const state = location.state as LocationState;

  const colors = useAppSelector((state) => state.theme.theme);
  const [loading, setLoading] = useState(false);
  const [expertData, setExpertData] = useState<Expert | null>(null);
  const [bookingSuccess, setBookingSuccess] = useState(false);

  const fyntraiCharge = 100;
  const tax = 0;

  useEffect(() => {
    if (!state) {
      navigate(`/expert/${id}/book`);
      return;
    }
    loadExpertData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadExpertData = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await fetchExpertDetails(parseInt(id));
      setExpertData(data);
    } catch (error) {
      console.error('Error fetching expert data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSessionRate = () => {
    if (!expertData) return 0;
    return state?.bookingType === 'C'
      ? expertData.chat_session_rate
      : expertData.call_session_rate;
  };

  const getTotalAmount = () => {
    return getSessionRate() + fyntraiCharge + tax;
  };

  if (!expertData && !loading) {
    return null;
  }

  const formatDateTime = (isoString: string) => {
    const date = new Date(isoString);
    return {
      date: date.toLocaleDateString('en-IN', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }),
    };
  };

  const handleProceed = async () => {
    if (!id || !state) return;
    setLoading(true);
    try {
      // Create booking order for payment
      const order = await createBookingOrder(parseInt(id), state.bookingType);

      // For now, simulate payment success and create booking
      // In production, integrate with Razorpay here using order.order_id
      await createExpertBooking(parseInt(id), {
        start_time: state.selectedSlotDate,
        end_time: state.selectedSlotEndDate,
        booking_type: state.bookingType,
        razorpay_res: { order_id: order.order_id, status: 'success' },
      });

      setBookingSuccess(true);
    } catch (error) {
      console.error('Error creating booking:', error);
      alert('Failed to create booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoToBookings = () => {
    navigate('/experts');
  };

  if (!state) {
    return null;
  }

  const { date, time } = formatDateTime(state.selectedSlotDate);
  const endTime = formatDateTime(state.selectedSlotEndDate).time;

  // Success Screen
  if (bookingSuccess) {
    return (
      <div
        className="p-4 min-h-screen pb-24 flex flex-col items-center justify-center"
        style={{ backgroundColor: colors.backgroundColor }}
      >
        <div
          className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
          style={{ backgroundColor: colors.green + '20' }}
        >
          {React.createElement(FiCheck as any, { size: 40, color: colors.green })}
        </div>

        <CText type="B24" color={colors.textColor} style={{ marginBottom: 8, textAlign: 'center' }}>
          Booking Confirmed!
        </CText>

        <CText type="r14" color={colors.grayScale500} style={{ marginBottom: 24, textAlign: 'center' }}>
          Your appointment with {expertData?.name} has been booked successfully.
        </CText>

        <div
          className="w-full rounded-xl p-4 mb-6"
          style={{ backgroundColor: colors.inputBackground }}
        >
          <div className="flex justify-between mb-2">
            <CText type="r14" color={colors.grayScale500}>Date</CText>
            <CText type="M14" color={colors.textColor}>{date}</CText>
          </div>
          <div className="flex justify-between mb-2">
            <CText type="r14" color={colors.grayScale500}>Time</CText>
            <CText type="M14" color={colors.textColor}>{time} - {endTime}</CText>
          </div>
          <div className="flex justify-between">
            <CText type="r14" color={colors.grayScale500}>Type</CText>
            <CText type="M14" color={colors.textColor}>
              {state.bookingType === 'C' ? 'Chat Session' : 'Video Call'}
            </CText>
          </div>
        </div>

        <CButton title="View My Bookings" onPress={handleGoToBookings} />
      </div>
    );
  }

  return (
    <div
      className="p-4 min-h-screen pb-24"
      style={{ backgroundColor: colors.backgroundColor }}
    >
      {loading && <CLoader />}

      <CHeader title="Order Summary" />

      {/* Expert Info Card */}
      <div
        className="rounded-xl p-4 mb-4 flex items-center gap-4"
        style={{ backgroundColor: colors.inputBackground }}
      >
        <div
          className="w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold"
          style={{ backgroundColor: colors.primary, color: colors.white }}
        >
          {expertData?.name?.charAt(0) || 'E'}
        </div>
        <div className="flex-1">
          <CText type="B16" color={colors.textColor}>
            {expertData?.name || 'Expert'}
          </CText>
          <CText type="r12" color={colors.grayScale500}>
            {expertData?.sebi_qualification || ''}
          </CText>
          <CText type="r12" color={colors.grayScale500}>
            30 mins {state.bookingType === 'C' ? 'Chat' : 'Video Call'}
          </CText>
        </div>
        <CText type="B18" color={colors.textColor}>
          ₹{getSessionRate()}
        </CText>
      </div>

      {/* Booking Details */}
      <div
        className="rounded-xl p-4 mb-4"
        style={{ backgroundColor: colors.inputBackground }}
      >
        <CText type="M14" color={colors.textColor} style={{ marginBottom: 12 }}>
          Booking Details
        </CText>
        <div className="flex justify-between mb-2">
          <CText type="r14" color={colors.grayScale500}>Date</CText>
          <CText type="r14" color={colors.textColor}>{date}</CText>
        </div>
        <div className="flex justify-between">
          <CText type="r14" color={colors.grayScale500}>Time</CText>
          <CText type="r14" color={colors.textColor}>{time} - {endTime}</CText>
        </div>
      </div>

      {/* Order Summary */}
      <div
        className="rounded-xl p-4 mb-6"
        style={{ backgroundColor: colors.inputBackground }}
      >
        <CText type="M14" color={colors.textColor} style={{ marginBottom: 12 }}>
          Order Summary
        </CText>

        <div className="flex justify-between mb-2">
          <CText type="r14" color={colors.grayScale500}>Expert Charges</CText>
          <CText type="r14" color={colors.textColor}>₹{getSessionRate().toFixed(2)}</CText>
        </div>

        <div className="flex justify-between mb-2">
          <CText type="r14" color={colors.grayScale500}>Platform Fee</CText>
          <CText type="r14" color={colors.textColor}>₹{fyntraiCharge.toFixed(2)}</CText>
        </div>

        <div className="flex justify-between mb-2">
          <CText type="r14" color={colors.grayScale500}>Tax</CText>
          <CText type="r14" color={colors.textColor}>₹{tax.toFixed(2)}</CText>
        </div>

        <div
          className="flex justify-between pt-3 mt-3 border-t"
          style={{ borderColor: colors.borderColor }}
        >
          <CText type="B14" color={colors.textColor}>Total</CText>
          <CText type="B16" color={colors.primary}>₹{getTotalAmount().toFixed(2)}</CText>
        </div>
      </div>

      {/* Proceed Button */}
      <CButton title="Pay & Confirm" onPress={handleProceed} />

      <CText
        type="r12"
        color={colors.grayScale500}
        style={{ marginTop: 12, textAlign: 'center' }}
      >
        By proceeding, you agree to our Terms & Conditions
      </CText>
    </div>
  );
};

export default BookingConfirmation;
